import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import React, { PropsWithChildren, useState } from 'react';
import { useGetMetaradarRefreshStatusQuery, useGetMetaradarStateSnapshotQuery } from '@app/model/metaradar/service/MetaradarServiceHooks';
import { MetaradarRefreshState } from '@app/model/metaradar/MetaradarRefreshStatus';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import MetaradarDailyReportUndercutsAnalysisFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__metaradar/fragment/MetaradarDailyReportUndercutsAnalysisFragment';
import MetaradarDailyReportUndercutPerProviderFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__metaradar/fragment/MetaradarDailyReportUndercutPerProviderFragment';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import MetaradarGraphUndercutsPerRegionForProvidersFragment
	from './MetaradarGraphUndercutsPerRegionForProvidersFragment';
import { ClockIcon } from '@heroicons/react/24/outline';
import Utilities from '@util/Utilities';
import MetaradarSummaryFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__metaradar/fragment/MetaradarSummaryFragment';
import RefreshMetaradarButton from './RefreshMetaradarButton';


const MetaradarDailyReportFragment: React.FunctionComponent<{ onOpenSettings: () => void }> = ({ onOpenSettings }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const metaradarStateSnapshotQuery = useGetMetaradarStateSnapshotQuery(companyHotel.id);
	const refreshStatusQuery = useGetMetaradarRefreshStatusQuery(companyHotel.id);

	if (metaradarStateSnapshotQuery.isLoading) {
		return <TablePlaceholder />;
	}

	if (metaradarStateSnapshotQuery.isSuccess) {

		if (!metaradarStateSnapshotQuery.data) {
			return <div>
				<div className="flex justify-end items-center gap-2 mb-2">
					<RefreshMetaradarButton companyHotelId={companyHotel.id}>
						<a href="#" onClick={onOpenSettings}
						   className="font-medium text-xs text-blue-700 underline hover:text-blue-600">Configure Metaradar</a>
					</RefreshMetaradarButton>
				</div>
				<Message state="info">{refreshStatusQuery.data?.status === MetaradarRefreshState.IN_PROGRESS
					? 'Results will appear here when the refresh finishes.'
					: 'No metasearch check yet. Press Refresh to run one.'}</Message>
			</div>;
		}

		const daysCount = metaradarStateSnapshotQuery.data.dailyItems.length;


		return <div>
			<div className="w-full flex justify-end items-center gap-2">
				<RefreshMetaradarButton companyHotelId={companyHotel.id}>
					<span className="text-xs text-gray-400 flex items-center">
	            <ClockIcon className="w-4 h-4 mr-1" />
							<span>{`Updated ${Utilities.timeDifference(metaradarStateSnapshotQuery.data.lastUpdateDate)} ago`}</span>
					</span>
					<a href="#" onClick={onOpenSettings}
					   className="font-medium text-xs text-blue-700 underline hover:text-blue-600">Configure Metaradar</a>
				</RefreshMetaradarButton>
			</div>

			<div className="md:flex items-center flex-wrap justify-between gap-y-1 gap-x-2 mb-2 mt-4">
				<p className="text-xs text-gray-400">
					• Showing results for the next {daysCount} days
					{companyHotel.metaradarSettings?.nights ? ` | Nights: ${companyHotel.metaradarSettings.nights}` : ''}
					{companyHotel.metaradarSettings?.threshold && companyHotel.metaradarSettings?.threshold > 0 ? ` | Applied Threshold: ${companyHotel.metaradarSettings.threshold} ${companyHotel.settings.main.currency}` : ''}
				</p>
			</div>
			<div className="space-y-4">
				<Section title="Summary">
					<MetaradarSummaryFragment metaradarStateSnapshot={metaradarStateSnapshotQuery.data} />
				</Section>

				<Section title="Undercut per Channel">
					<MetaradarDailyReportUndercutPerProviderFragment metaradarStateSnapshot={metaradarStateSnapshotQuery.data} />
				</Section>

				<Section title="Undercuts per Region">
					<MetaradarGraphUndercutsPerRegionForProvidersFragment
						metaradarStateSnapshot={metaradarStateSnapshotQuery.data} />
				</Section>
				<Section title="Undercuts Analysis">
					<MetaradarDailyReportUndercutsAnalysisFragment metaradarStateSnapshot={metaradarStateSnapshotQuery.data} />
				</Section>
			</div>
		</div>;
	}

	return <div>
		<div className="flex justify-end items-center gap-2 mb-2">
			<RefreshMetaradarButton companyHotelId={companyHotel.id}>
				<a href="#" onClick={onOpenSettings}
				   className="font-medium text-xs text-blue-700 underline hover:text-blue-600">Configure Metaradar</a>
			</RefreshMetaradarButton>
		</div>
		<Message state="warning">Metaradar Daily Report does not exist yet!</Message>
	</div>;

};


const Section: React.FunctionComponent<PropsWithChildren<{ title: string | React.ReactNode }>> = ({
																																																		title,
																																																		children,
																																																	}) => {

	const [isOpen, setIsOpen] = useState(true);

	return <div className="bg-white shadow sm:overflow-hidden sm:rounded-lg">
		<div className="divide-y divide-gray-200 cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
			<div className="px-4 py-4 sm:px-6">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold leading-7 text-gray-900">
						{title}
					</h2>
					{isOpen && <ChevronDownIcon className="w-6 h-6 text-gray-400" onClick={() => setIsOpen(false)} />}
					{!isOpen && <ChevronUpIcon className="w-6 h-6 text-gray-400" onClick={() => setIsOpen(true)} />}
				</div>
			</div>
		</div>
		{isOpen && children}
	</div>;
};


export default MetaradarDailyReportFragment;
